import { useSignal, useSignalEffect } from "@preact/signals-react";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { INGAssistantProps } from "../../library/NGFieldExtensions";
import { getStyleObject, getTestId } from "../../library/utils";
import { MicrophoneVisualizer } from "./MicrophoneVisualizer";

import Icon from "../NGMaterialIcon/NGMaterialIcon";
import { Box, IconButton, Menu, MenuItem, Switch, Tooltip, Typography } from "@mui/material";
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AssistantMode, AssistantTone } from "../../../resolvers-types";
import NGLayoutItem from "../../generators/NGLayoutItem";
import { log } from "../../library/logger";
import { getTypename } from "../../library/metadataUtils";
import { AssistantLabelKey, DEFAULT_ASSISTANT_LABELS } from "../NGAssistantLabelEditor/NGAssistantLabelEditorConstants";
import NGImage from "../NGImage/NGImage";
import { createChatModelAdapter } from "./NGAssistantAdapter";
import { TextToSpeechController, playStreamingTextToSpeech } from "./textToSpeech";

interface SpeechRecognitionInterface {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives?: number;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent {
  error: string;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInterface;
    webkitSpeechRecognition: new () => SpeechRecognitionInterface;
  }
}

const tag = "NGAssistant";
export function NGAssistant({ config, context }: INGAssistantProps) {
  const assistantUIReact: any = useSignal(null);
  const assistantUIReactMarkdown: any = useSignal(null);
  useEffect(() => {
    (async () => {
      const reactMod = await import("@assistant-ui/react");
      const reactMarkdownMod = await import("@assistant-ui/react-markdown");
      assistantUIReact.value = reactMod;
      assistantUIReactMarkdown.value = reactMarkdownMod;
    })();
  }, []);

  if (!assistantUIReact.value || !assistantUIReactMarkdown.value) return <div>Loading flow…</div>;

  return (
    <NGAssistantRender
      config={config}
      context={context}
      assistantUIReact={assistantUIReact.value}
      assistantUIReactMarkdown={assistantUIReactMarkdown.value}
    />
  );
}
const VoiceAwareAssistantMessage = React.memo(
  ({
    voiceModeIsOn,
    TextComponent,
    assistantUIReact,
  }: {
    voiceModeIsOn: boolean;
    TextComponent: any;
    assistantUIReact: any;
  }) => {
    const { MessagePrimitive, useMessage, useThread } = assistantUIReact;
    const message = useMessage();
    const thread = useThread();

    // In voice mode, only show the most recent assistant message
    if (voiceModeIsOn) {
      const messages = thread.messages;
      const assistantMessages = messages.filter((m) => m.role === "assistant");
      if (assistantMessages.length > 0) {
        const mostRecentAssistantMessage = assistantMessages[assistantMessages.length - 1];
        if (message.id !== mostRecentAssistantMessage.id) {
          return null;
        }
      }
    }

    return (
      <MessagePrimitive.Root className={voiceModeIsOn ? "message-assistant-voice" : "message-assistant"}>
        <MessagePrimitive.Content
          components={{
            Text: TextComponent,
          }}
        />
      </MessagePrimitive.Root>
    );
  }
);

export function NGAssistantRender({ config, context, assistantUIReact, assistantUIReactMarkdown }: INGAssistantProps) {
  const local = setupLocalState(
    config,
    {
      Classes: useSignal(config.Classes ?? []),
      Disabled: useSignal(config.Disabled ?? false),
      Style: useSignal(config.Style ?? {}),
      Messages: useSignal(config.Messages ?? null),
      Value: useSignal(config.Value ?? null),
      Visible: useSignal(config.Visible ?? true),
      WelcomeImage: useSignal(config.WelcomeImage ?? null),
      Mode: useSignal<AssistantMode>(config.Mode ?? AssistantMode.DeterministicPreferred),
      Tone: useSignal<AssistantTone>(config.Tone ?? AssistantTone.Advisor),
      AssistantLabelEditor: useSignal(config.AssistantLabelEditor ?? DEFAULT_ASSISTANT_LABELS),
      VoiceModeEnabled: useSignal(config.VoiceModeEnabled ?? false),
      ListeningIsInitialized: useSignal(false),
      ListeningIsEnabled: useSignal(false),
      TTSController: useSignal<TextToSpeechController | null>(null),
      SpeechRecognition: useSignal<SpeechRecognitionInterface | null>(null),
      ShowSuggestions: useSignal(config.ShowSuggestions ?? true),
    },
    context
  );
  const { AssistantRuntimeProvider, useLocalRuntime } = assistantUIReact;
  const handlers = setupHandlers(config, context);

  const tts = playStreamingTextToSpeech({
    onPlay: () => log.debug(tag, "TTS playback started"),
    onEnd: () => log.debug(tag, "TTS playback ended"),
    onError: (error) => log.error(tag, "TTS error:", error),
  });
  local.TTSController.value = tts.controller;
  const runtime = useLocalRuntime(createChatModelAdapter(local.Value, local.Messages, tts));

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <AssistantContent
        local={local}
        context={context}
        config={config}
        handlers={handlers}
        assistantUIReact={assistantUIReact}
        assistantUIReactMarkdown={assistantUIReactMarkdown}
      />
    </AssistantRuntimeProvider>
  );
}

interface AssistantContentProps {
  local: any;
  context: any;
  config: any;
  handlers: any;
  assistantUIReact: any;
  assistantUIReactMarkdown: any;
}

function AssistantContent({
  local,
  context,
  config,
  handlers,
  assistantUIReact,
  assistantUIReactMarkdown,
}: AssistantContentProps) {
  const { ComposerPrimitive, ThreadPrimitive, useComposerRuntime, useThread, useThreadRuntime } = assistantUIReact;
  const { MarkdownTextPrimitive, unstable_memoizeMarkdownComponents: memoizeMarkdownComponents } =
    assistantUIReactMarkdown;

  const lastItemRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const composerRuntime = useComposerRuntime();
  const inputFocused = useSignal(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [dynamicPadding, setDynamicPadding] = useState(0);

  const handleInputFocus = () => {
    // Clear any pending blur timeout when focusing
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    inputFocused.value = true;
  };

  const handleInputBlur = () => {
    // Delay closing to allow suggestion clicks to process
    blurTimeoutRef.current = setTimeout(() => {
      inputFocused.value = false;
    }, 150);
  };

  // Effect to handle voice transcripts
  const checkForVoiceInput = () => {
    if (!local.VoiceModeEnabled.value) return;

    if (local.Value.value && typeof local.Value.value === "string") {
      log.debug(tag, "Voice transcript received:", local.Value.value);

      // Stop recognition before sending to avoid stacking
      if (local.SpeechRecognition.value && local.ListeningIsEnabled.value) {
        try {
          local.SpeechRecognition.value.stop();
          local.ListeningIsEnabled.value = false;
        } catch (e) {
          log.error(tag, "Error stopping recognition before send:", e);
        }
      }

      if (isRunning) {
        composerRuntime.cancel();
        threadRuntime.cancelRun();
      }
      stopVoiceInput();
      local.TTSController.value.cancel();

      // Send the message
      composerRuntime.setText(local.Value.value);
      composerRuntime.send();
      log.debug(tag, "Message sent using voice");

      // Clear the value after processing
      local.Value.value = null;

      // Restart listening if voice mode is still on
      if (local.VoiceModeEnabled.value) {
        startVoiceRecognition();
      } else {
        stopVoiceInput();
      }

      setInterval(checkForVoiceInput, 100);
    }
  };

  useSignalEffect(() => {
    local.VoiceModeEnabled.value ? startVoiceInput() : stopVoiceInput();
  });

  // Voice functionality setup
  const initializeListening = () => {
    log.debug(tag, "Initializing voice functionality");

    // Initialize speech recognition
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";
      recognition.maxAlternatives = 3;

      recognition.onstart = () => {
        local.ListeningIsInitialized.value = true;
        local.ListeningIsEnabled.value = true;
        log.debug(tag, "Listening initialized");
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        // Process all results, looking for final results
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];

          if (result.isFinal) {
            const transcript = result[0].transcript;

            // Set the transcript to trigger the message send
            if (transcript.trim()) {
              local.Value.value = transcript.trim();
              checkForVoiceInput();
              log.debug(tag, "Final transcript received:", transcript.trim());

              // Restart listening if voice mode is still on
              startVoiceRecognition();
            } else {
              log.debug(tag, "Transcript is empty after trim");
              // Still restart listening for empty results if in voice mode
              startVoiceRecognition();
            }
            break;
          }
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        local.ListeningIsEnabled.value = false;
        log.error(tag, "Speech recognition error:", event.error);

        // Only restart if voice mode is still enabled
        if (local.VoiceModeEnabled.value) {
          startVoiceRecognition();
        }
      };

      recognition.onend = () => {
        local.ListeningIsEnabled.value = false;
        log.debug(tag, "Speech recognition ended");
        // Only restart if voice mode is still enabled
        if (local.VoiceModeEnabled.value) {
          startVoiceRecognition();
        }
      };

      local.SpeechRecognition.value = recognition;
    } else {
      log.warn(tag, "Speech recognition not supported in this browser");
      log.debug(tag, "Checking for APIs - SpeechRecognition:", "SpeechRecognition" in window);
      log.debug(tag, "Checking for APIs - webkitSpeechRecognition:", "webkitSpeechRecognition" in window);
    }
  };

  // Voice control functions
  const startVoiceInput = () => {
    if (!local.SpeechRecognition.value) {
      initializeListening();
    }

    if (local.SpeechRecognition.value) {
      try {
        // Check microphone permissions first
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          navigator.mediaDevices
            .getUserMedia({ audio: { echoCancellation: true } })
            .then(() => {
              startVoiceRecognition();
            })
            .catch((error) => {
              log.error(tag, "Microphone permission denied or error:", error);
            });
        }
      } catch (error) {
        log.error(tag, "Error starting speech recognition:", error);
      }
    } else {
      if (!local.SpeechRecognition.value) {
        log.debug(tag, "Cannot start - local.SpeechRecognition.value is null");
      }
    }
  };

  const startVoiceRecognition = () => {
    log.debug(tag, "Starting voice recognition");
    if (local.VoiceModeEnabled.value && local.SpeechRecognition.value) {
      // Prevent multiple streams
      if (local.ListeningIsEnabled.value) {
        return;
      }
      try {
        local.SpeechRecognition.value.start();
        local.ListeningIsEnabled.value = true;
      } catch (error) {
        // Voice recognition is already started
      }
    }
  };

  const stopVoiceInput = () => {
    local.ListeningIsInitialized.value = false;
    if (local.SpeechRecognition.value) {
      try {
        log.debug(tag, "Stopping speech recognition");
        local.SpeechRecognition.value.abort();
      } catch (e) {
        log.debug(tag, "Cannot stop - recognitionRef.current is null");
      }
      local.ListeningIsEnabled.value = false;
    }
  };

  const toggleVoiceMode = () => {
    const wasOn = local.VoiceModeEnabled.value;
    local.VoiceModeEnabled.value = !wasOn;
  };
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null);
  const openSettingsMenu = (event: MouseEvent<HTMLElement>) => setSettingsAnchorEl(event.currentTarget);
  const closeSettingsMenu = () => setSettingsAnchorEl(null);

  const [hasUnreadContent, setHasUnreadContent] = useState(false);
  const { isRunning, messages } = useThread();
  const threadRuntime = useThreadRuntime();

  const viewportRef = useRef<HTMLDivElement>(null);
  const prevIsRunningRef = useRef(isRunning);

  const messageCountByUser = messages.filter((m) => m.role === "user").length;
  const previousMessageCountByUser = useRef(0);

  // Function to calculate dynamic padding
  const calculateDynamicPadding = useCallback(() => {
    const viewport = viewportRef.current;
    const spacing = messages.length === 2 ? 64 : 16;

    if (viewport) {
      const viewportHeight = viewport.clientHeight;
      const padding = Math.max(viewportHeight - spacing, 0);
      setDynamicPadding(padding);
    }
  }, [messages.length]);

  useEffect(() => {
    if (messageCountByUser > previousMessageCountByUser.current) {
      // Clear any pending blur timeout and close overlay immediately on send
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      inputFocused.value = false;

      // Calculate dynamic padding after message is rendered
      setTimeout(() => {
        calculateDynamicPadding();
      }, 100);

      handlers["onSend"]?.();
    }

    previousMessageCountByUser.current = messageCountByUser;
  }, [messageCountByUser, handlers, inputFocused, calculateDynamicPadding]);

  const startedRunning = isRunning && !prevIsRunningRef.current;

  const suggestionsFromModel = useRef<string[]>([]);

  // Debounced scroll function using lodash-es
  const scrollIntoView = useCallback(({ delayMs }: { delayMs: number }) => {
    log.debug(tag, "scrollIntoView 2");
    setTimeout(() => {
      lastItemRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      setHasUnreadContent(false);
    }, delayMs);
  }, []);

  // Store previous references for comparison
  const prevRefsRef = useRef<{
    textComponent: any;
    customComponents: any;
    defaultComponents: any;
  }>({
    textComponent: null,
    customComponents: null,
    defaultComponents: null,
  });

  // Memoize customComponents to prevent recreation on every render
  const customComponents: Record<string, React.ComponentType<{ config: string }>> = useMemo(
    () => ({
      nglayoutitem: ({ config }: { config: string }) => {
        try {
          const parsedConfig = JSON.parse(config);
          return <NGLayoutItem key={parsedConfig.Id} config={parsedConfig} context={context} />;
        } catch (error) {
          log.error(tag, "JSON parse error:", error);
          log.error(tag, "Failed to parse config:", JSON.stringify(config));
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          return <div>Error parsing config: {errorMessage}</div>;
        }
      },
      ngsuggestions: ({ config }: { config: string }) => {
        try {
          const parsedConfig = JSON.parse(config);
          const suggestionsFromModel = Array.isArray(parsedConfig.Suggestions) ? parsedConfig.Suggestions : [];

          return local.ShowSuggestions.value ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: "center",
                justifyContent: "center",
                my: 8,
              }}
            >
              <Box className="suggestions-from-model-container">
                {suggestionsFromModel.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="h4">{getAssistantLabel("Suggestions Title")}</Typography>
                    <Box component="ul" sx={{ listStyleType: "none !important", p: 0, m: "0 !important" }}>
                      {suggestionsFromModel.map((suggestion, index) => (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "stretch",
                            justifyContent: "stretch",
                            borderBottom: "1px solid lightgray",
                            borderTop: index === 0 ? "1px solid lightgray" : "none",
                          }}
                          component="li"
                          key={index}
                        >
                          <Box
                            component={ThreadPrimitive.Suggestion}
                            sx={{
                              all: "unset",
                              display: "flex",
                              justifyContent: "space-between",
                              flex: 1,
                              padding: "8px 0",
                              cursor: "pointer",
                              "& *": {
                                transition: "color 0.2s ease",
                              },
                              "&:hover": {
                                color: "var(--md-sys-color-primary)",
                              },
                            }}
                            prompt={suggestion}
                            method="replace"
                            autoSend
                          >
                            {suggestion}
                            <Box sx={{ display: "flex", alignItems: "center", color: "var(--md-sys-color-primary)" }}>
                              <ArrowUpRightIcon />
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            ""
          );
        } catch (error) {
          log.error(tag, "JSON parse error:", error);
          log.error(tag, "Failed to parse config:", JSON.stringify(config));
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          return <div>Error parsing config: {errorMessage}</div>;
        }
      },
    }),
    [context, local.ShowSuggestions]
  );

  const AssistantMessage = useCallback(() => {
    const TextComponent = ({ text: _text }) => {
      const defaultComponents = useMemo(() => memoizeMarkdownComponents(customComponents), [customComponents]);

      // Log object recreation with "render" keyword for filtering
      log.debug(tag, `TextComponent render - same as previous: ${TextComponent === prevRefsRef.current.textComponent}`);
      log.debug(
        tag,
        `customComponents render - same as previous: ${customComponents === prevRefsRef.current.customComponents}`
      );
      log.debug(
        tag,
        `defaultComponents render - same as previous: ${defaultComponents === prevRefsRef.current.defaultComponents}`
      );

      // Update references for next comparison
      prevRefsRef.current.textComponent = TextComponent;
      prevRefsRef.current.customComponents = customComponents;
      prevRefsRef.current.defaultComponents = defaultComponents;

      return (
        <MarkdownTextPrimitive
          smooth
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          className="aui-md"
          components={defaultComponents}
        />
      );
    };

    return (
      <VoiceAwareAssistantMessage
        voiceModeIsOn={local.VoiceModeEnabled.value}
        TextComponent={TextComponent}
        assistantUIReact={assistantUIReact}
      />
    );
  }, [local.VoiceModeEnabled.value, customComponents]);

  const StableUserMessage = useCallback(() => {
    return (
      <UserMessage
        voiceModeIsOn={local.VoiceModeEnabled.value}
        assistantUIReact={assistantUIReact}
        ref={lastUserMessageRef}
      />
    );
  }, [local.VoiceModeEnabled.value]);

  useEffect(() => {
    log.debug(tag, `startedRunning changed: -> ${startedRunning}`);

    if (startedRunning) {
      // new run, clear suggestions
      suggestionsFromModel.current = [];

      // we need to wait for the animation before setting it
      // otherwise scrollIntoView will reset the unread content flag
      setTimeout(() => {
        setHasUnreadContent(true);
      }, 250);
    }
  }, [startedRunning]);

  // Reset padding when stream ends
  useEffect(() => {
    if (!isRunning) {
      setDynamicPadding(0);
    }
  }, [isRunning]);

  useEffect(() => {
    if (startedRunning) {
      scrollIntoView({ delayMs: 350 });
    }
  }, [scrollIntoView, startedRunning]);

  const getAssistantLabel = useCallback(
    (key: AssistantLabelKey): string => {
      return local.AssistantLabelEditor.value.find((o) => o.label === key)?.value ?? "";
    },
    [local.AssistantLabelEditor]
  );

  const suggestions = [getAssistantLabel("Suggestion 1"), getAssistantLabel("Suggestion 2")].filter(Boolean);

  const canShowInitialSuggestions = messages.length === 0;

  return (
    <ThreadPrimitive.Root
      style={getStyleObject(local.Style.value)}
      data-testid={getTestId(config)}
      data-type={getTypename(config)}
    >
      <ThreadPrimitive.Viewport ref={viewportRef} className="thread-viewport card">
        {/* empty state welcome message */}
        <ThreadPrimitive.Empty>
          <Box className="thread-empty">
            <Box className="thread-empty-content">
              {local.WelcomeImage.value && !local.VoiceModeEnabled.value && (
                <NGImage context={context} config={local.WelcomeImage.value} />
              )}
              {!local.VoiceModeEnabled.value && <p className="welcome-text">{getAssistantLabel("Welcome")}</p>}
            </Box>
          </Box>
        </ThreadPrimitive.Empty>

        <MicrophoneVisualizer
          isActive={local.VoiceModeEnabled.value && local.ListeningIsInitialized.value}
          isThinking={isRunning}
          size={500}
          barCount={35}
          barWidth={6}
          barColor={"#1648a8"}
          centerRadius={75}
        />

        {/* messages */}
        <ThreadPrimitive.Messages
          components={{
            UserMessage: StableUserMessage,
            EditComposer,
            AssistantMessage,
          }}
        />

        <ThreadPrimitive.If empty={false}>
          <Box className="thread-not-empty" />
        </ThreadPrimitive.If>

        <Box
          sx={{
            width: "100%",
            paddingTop: isRunning ? `${dynamicPadding}px` : "0px",
            transition: "padding-top 0.3s ease-in-out",
          }}
          ref={lastItemRef}
        />
      </ThreadPrimitive.Viewport>

      {/* input area */}
      <Box className="composer-container" sx={{ position: "relative", zIndex: 999 }}>
        {/* suggestions focus Overlay */}
        {canShowInitialSuggestions && (
          <Box
            sx={{
              position: "absolute",
              bottom: "100%",
              left: 0,
              right: 0,
              maxWidth: "640px",
              margin: "0 auto",
              backgroundColor: "background.paper",
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "primary.main",
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderBottom: "none",
              overflow: "hidden",
              transform: inputFocused.value ? "translateY(0)" : "translateY(100px)",
              opacity: inputFocused.value ? 1 : 0,
              transition: inputFocused.value
                ? "transform 200ms ease-out, opacity 200ms ease-out"
                : "transform 150ms ease-in, opacity 150ms ease-in",
              pointerEvents: inputFocused.value ? "auto" : "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 0.5,
                px: 1.5,
                py: 1.5,
                cursor: "pointer",
              }}
            >
              <ThreadPrimitive.If empty>
                {suggestions.map((suggestion, index) => (
                  <SuggestionItem
                    key={`suggestion-${index}`}
                    suggestion={suggestion}
                    index={index}
                    keyPrefix="suggestion"
                    ThreadPrimitive={ThreadPrimitive}
                  />
                ))}
              </ThreadPrimitive.If>
            </Box>
          </Box>
        )}

        <Box className="scroll-to-bottom-container">
          <ThreadPrimitive.If running={false}>
            <ThreadPrimitive.ScrollToBottom asChild className="scroll-to-bottom">
              <IconButton
                className="scroll-button"
                sx={
                  hasUnreadContent
                    ? { backgroundColor: "var(--md-sys-color-primary)", color: "var(--md-sys-color-on-primary)" }
                    : { backgroundColor: "var(--md-sys-color-on-primary)", color: "var(--md-sys-color-primary)" }
                }
                onClick={() => {
                  scrollIntoView({ delayMs: 0 });
                }}
              >
                <Icon name="arrow_downward" />
              </IconButton>
            </ThreadPrimitive.ScrollToBottom>
          </ThreadPrimitive.If>
        </Box>

        <Box sx={{ maxWidth: "640px", margin: "0 auto", backgroundColor: "#fff", position: "relative", zIndex: 999 }}>
          <ComposerPrimitive.Root
            className={`composer-root ${canShowInitialSuggestions && inputFocused.value ? "overlay-active" : ""}`}
          >
            <IconButton className="icon-button-settings button-primary-large" onClick={openSettingsMenu}>
              <Icon name="settings" />
            </IconButton>

            <Menu
              anchorEl={settingsAnchorEl}
              open={Boolean(settingsAnchorEl)}
              onClose={closeSettingsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  minWidth: 280,
                  maxWidth: 320,
                  "& .MuiList-root": {
                    py: 0,
                  },
                  "& .MuiMenuItem-root": {
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    minHeight: "auto",
                    py: 0.5,
                  },
                },
              }}
            >
              {/* Header with title and close button */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 16px 6px 16px", // Reduced padding
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                }}
              >
                <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                  Settings
                </Typography>
                <IconButton
                  className="icon-button-settings-close"
                  size="small"
                  onClick={closeSettingsMenu}
                  sx={{ ml: 1 }}
                >
                  <Icon name="close" fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ padding: "4px 0" }}>
                {/* Show Suggestions Section */}
                <MenuItem
                  onClick={() => {
                    local.ShowSuggestions.value = !local.ShowSuggestions.value;
                  }}
                  sx={{ pl: 2, py: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <Typography>Show suggestions</Typography>
                  <Switch checked={local.ShowSuggestions.value} />
                </MenuItem>
              </Box>
              {/* Divider */}
              <Box sx={{ height: "1px", backgroundColor: "rgba(0, 0, 0, 0.12)", margin: "4px 0" }} />
              {/* Mode Section */}
              <Box sx={{ padding: "4px 0" }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ px: 2, py: 0.5, color: "text.secondary", fontSize: "0.875rem" }}
                >
                  Mode
                </Typography>
                <MenuItem
                  onClick={() => {
                    local.Mode.value = AssistantMode.Deterministic;
                    closeSettingsMenu();
                  }}
                  sx={{ pl: 3, py: 0.5 }}
                >
                  {local.Mode.value === AssistantMode.Deterministic && "✓ "}
                  {getAssistantLabel("Deterministic")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    local.Mode.value = AssistantMode.DeterministicPreferred;
                    closeSettingsMenu();
                  }}
                  sx={{ pl: 3, py: 0.5, whiteSpace: "normal", wordWrap: "break-word", minHeight: "auto" }}
                >
                  <Box component="span" sx={{ lineHeight: 1.2 }}>
                    {local.Mode.value === AssistantMode.DeterministicPreferred && "✓ "}
                    {getAssistantLabel("Deterministic Preferred")}
                  </Box>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    local.Mode.value = AssistantMode.Generative;
                    closeSettingsMenu();
                  }}
                  sx={{ pl: 3, py: 0.5 }}
                >
                  {local.Mode.value === AssistantMode.Generative && "✓ "}
                  {getAssistantLabel("Generative")}
                </MenuItem>
              </Box>
              {/* Divider */}
              <Box sx={{ height: "1px", backgroundColor: "rgba(0, 0, 0, 0.12)", margin: "4px 0" }} />
              {/* Tone Section */}
              <Box sx={{ padding: "4px 0" }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ px: 2, py: 0.5, color: "text.secondary", fontSize: "0.875rem" }}
                >
                  Tone
                </Typography>
                <MenuItem
                  onClick={() => {
                    local.Tone.value = AssistantTone.Analyst;
                    closeSettingsMenu();
                  }}
                  sx={{ pl: 3, py: 0.5 }}
                >
                  {local.Tone.value === AssistantTone.Analyst && "✓ "}
                  {getAssistantLabel("Analyst")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    local.Tone.value = AssistantTone.Advisor;
                    closeSettingsMenu();
                  }}
                  sx={{ pl: 3, py: 0.5 }}
                >
                  {local.Tone.value === AssistantTone.Advisor && "✓ "}
                  {getAssistantLabel("Advisor")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    local.Tone.value = AssistantTone.Partner;
                    closeSettingsMenu();
                  }}
                  sx={{ pl: 3, py: 0.5 }}
                >
                  {local.Tone.value === AssistantTone.Partner && "✓ "}
                  {getAssistantLabel("Partner")}
                </MenuItem>
              </Box>
            </Menu>

            <ComposerPrimitive.Input
              autoFocus
              disabled={isRunning}
              className="composer-input"
              placeholder={isRunning ? "Working..." : "Write a message..."}
              rows={1}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              {/* Voice Mode Toggle Button */}
              <Tooltip title={local.VoiceModeEnabled.value ? "Disable Voice Mode" : "Enable Voice Mode"} arrow>
                <Box component="span">
                  <IconButton
                    className={local.VoiceModeEnabled.value ? "button-primary-large" : "button-secondary-large"}
                    onClick={toggleVoiceMode}
                  >
                    {local.VoiceModeEnabled.value ? <Icon name="volume_up" /> : <Icon name="volume_up" />}
                  </IconButton>
                </Box>
              </Tooltip>

              <ThreadPrimitive.If running={false}>
                <ComposerPrimitive.Send asChild>
                  <Tooltip title="Send" arrow>
                    <Box component="span">
                      <IconButton className="icon-button-send button-primary-large">
                        <Icon name="send" />
                      </IconButton>
                    </Box>
                  </Tooltip>
                </ComposerPrimitive.Send>
              </ThreadPrimitive.If>

              <ThreadPrimitive.If running>
                <ComposerPrimitive.Cancel asChild>
                  <Tooltip title="Cancel" arrow>
                    <Box component="span">
                      <IconButton
                        className="icon-button-cancel button-primary-large"
                        onClick={() => {
                          threadRuntime.cancelRun();
                          handlers["onCancel"]?.();
                        }}
                      >
                        <Icon name="stop" />
                      </IconButton>
                    </Box>
                  </Tooltip>
                </ComposerPrimitive.Cancel>
              </ThreadPrimitive.If>
            </Box>
          </ComposerPrimitive.Root>
        </Box>
      </Box>
    </ThreadPrimitive.Root>
  );
}

const SuggestionItem = ({
  suggestion,
  index,
  keyPrefix,
  ThreadPrimitive,
}: {
  suggestion: string;
  index: number;
  keyPrefix: string;
  ThreadPrimitive: any;
}) => {
  return (
    <ThreadPrimitive.Suggestion
      key={`${keyPrefix}-${index}`}
      className="suggestion"
      prompt={suggestion}
      method="replace"
      autoSend
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 1,
          py: 1,
          cursor: "pointer",
          borderRadius: "12px",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: "action.hover",
          },
          "&:hover .arrow-icon": {
            opacity: 1,
          },
        }}
      >
        <SearchIcon />
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            color: "text.primary",
            margin: "0 !important",
          }}
        >
          {suggestion}
        </Typography>
        <Box
          className="arrow-icon"
          sx={{
            opacity: 0,
            transition: "opacity 0.2s ease",
            color: "var(--md-sys-color-primary)",
          }}
        >
          <ArrowUpRightIcon />
        </Box>
      </Box>
    </ThreadPrimitive.Suggestion>
  );
};

const SearchIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="16"
      height="16"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
};

const ArrowUpRightIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="16"
      height="16"
    >
      <path d="M7 7h10v10" />
      <path d="M7 17 17 7" />
    </svg>
  );
};

function EditComposer() {
  return null;
}

const UserMessage = React.memo(
  React.forwardRef<HTMLDivElement, { voiceModeIsOn: boolean; assistantUIReact: any }>(
    ({ voiceModeIsOn, assistantUIReact }, ref) => {
      const { BranchPickerPrimitive, MessagePrimitive, ThreadPrimitive } = assistantUIReact;
      // Don't render user messages when voice mode is on
      if (voiceModeIsOn) {
        return null;
      }

      return (
        <Box className="message-user-container" ref={ref}>
          {/* thinking indicator */}
          <ThreadPrimitive.If running>
            <Box className="thinking-indicator">
              <Box className="thinking-spinner"></Box>
            </Box>
          </ThreadPrimitive.If>

          {/* message */}
          <MessagePrimitive.Root className="message-user">
            <Box>
              <MessagePrimitive.Content />
            </Box>

            <BranchPickerPrimitive.Root hideWhenSingleBranch className="">
              <BranchPickerPrimitive.Previous asChild>
                <Tooltip title="Previous">
                  <Box component="span">
                    <IconButton>
                      <Icon name="chevron_left" />
                    </IconButton>
                  </Box>
                </Tooltip>
              </BranchPickerPrimitive.Previous>
              <Box component="span" className="font-medium">
                <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
              </Box>
              <BranchPickerPrimitive.Next asChild>
                <Tooltip title="Next">
                  <Box component="span">
                    <Icon name="chevron_right" />
                  </Box>
                </Tooltip>
              </BranchPickerPrimitive.Next>
            </BranchPickerPrimitive.Root>
          </MessagePrimitive.Root>
        </Box>
      );
    }
  )
);
